/**
 * Utility functions for phone number formatting
 */

/**
 * Formats a phone number to a readable format
 * Handles various formats including international numbers
 * 
 * Examples:
 * - +12259551002 -> +1 (225) 955-1002
 * - 12259551002 -> +1 (225) 955-1002
 * - 2259551002 -> (225) 955-1002
 * - +85212345678 -> +852 1234 5678 (Hong Kong)
 * - +8612345678901 -> +86 123 4567 8901 (China)
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone || typeof phone !== 'string') {
    return phone || '';
  }

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Handle empty or very short numbers
  if (cleaned.length < 3) {
    return phone;
  }

  // Handle US/Canada numbers (with or without +1)
  if (cleaned.startsWith('+1') || cleaned.startsWith('1') || (!cleaned.startsWith('+') && cleaned.length === 10)) {
    let digits = cleaned.replace(/^\+?1?/, '');
    
    // If we don't have 10 digits, return original
    if (digits.length !== 10) {
      return phone;
    }

    // Format as (XXX) XXX-XXXX
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Handle international numbers with country code
  if (cleaned.startsWith('+')) {
    const countryCode = cleaned.match(/^\+\d{1,3}/)?.[0];
    if (countryCode) {
      const remaining = cleaned.slice(countryCode.length);
      
      // Hong Kong (+852)
      if (countryCode === '+852') {
        if (remaining.length === 8) {
          return `${countryCode} ${remaining.slice(0, 4)} ${remaining.slice(4)}`;
        }
      }
      
      // China (+86)
      if (countryCode === '+86') {
        if (remaining.length === 11) {
          return `${countryCode} ${remaining.slice(0, 3)} ${remaining.slice(3, 7)} ${remaining.slice(7)}`;
        }
      }
      
      // Generic international format
      // Group remaining digits in groups of 3-4
      const formatted = remaining.match(/.{1,4}/g)?.join(' ') || remaining;
      return `${countryCode} ${formatted}`;
    }
  }

  // Handle 10-digit US numbers without country code
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // For other formats, return original or a simple spaced format
  if (cleaned.length > 6) {
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
  }

  return phone;
};

